import React from "react"
import { Button, FormFeedback, FormGroup, Input, Label, Table } from "reactstrap"
import { dimensionsLabel } from "pages/Logistics/Settings/settingsForm"
import { productsWeightOf } from "./shipmentLabels"

// ——— The parcels of a shipment on its AWB (CONTEXT „Colet”, „Ambalaj de expediere”; ticket 20 Q2, Q11, Q12) ———
// The same selector serves the AI prompt (US 97) and Cargus's generation (E4). The API checks the same rules.

export const PARCELS_LIMIT = 99
const ENVELOPES_LIMIT = 9
const WEIGHT_LIMIT = 100
const ENVELOPE_WEIGHT_LIMIT = 1

// Whether a shipment sends only documents, which an envelope may carry: diplomas alone. A text may describe anything.
export const documentsOnly = ({ content, text }) => content.length > 0 && !text && content.every(item => item.family === "diplomas")
// The packages a parcel may take: the active ones of Settings, an envelope only for documents.
const offered = (packages, documents) => packages.filter(item => item.active).map(item => ({ ...item, allowed: item.type === "box" || documents }))
const round = weight => Math.round(weight * 100) / 100
// The package of Settings a parcel is in.
const packageOf = (packages, parcel) => packages.find(item => item.id === parcel.packageId)
const isEnvelope = (packages, parcel) => packageOf(packages, parcel)?.type === "envelope"

// The package a new parcel starts in: the default box, or the first package it may take.
const firstPackage = (packages, documents) => {
  const allowed = offered(packages, documents).filter(item => item.allowed)
  return allowed.find(item => item.default) || allowed[0] || null
}
// What a shipment's parcels start from: what its products weigh, and the weight an administrator set after weighing it, packages
// included (US 96).
export const parcelWeights = shipment => ({ products: productsWeightOf(shipment), weighed: shipment.setWeight ?? null })
// Each parcel weighs an equal share of the products plus its own package, or an equal share of the weight set after weighing; a weight
// entered in the dialog stays until the parcels change.
const split = (parcels, packages, { products, weighed }) => parcels.map(parcel => {
  const own = weighed != null ? 0 : packageOf(packages, parcel)?.weight || 0
  return { ...parcel, weight: String(round((weighed ?? products ?? 0) / parcels.length + own)) }
})

// A shipment's first selection: one parcel in the default box, not delivered on Saturday; none without a package to take.
export const initialSelection = (packages, { weights, documents }) => {
  const chosen = firstPackage(packages, documents)
  return { parcels: chosen ? split([{ packageId: chosen.id }], packages, weights) : [], saturday: false }
}

// What is wrong with each parcel, by its index, and with the parcels together: a weight from 0.01 to 100 kg, an envelope up to 1 kg, and at
// most 9 envelopes on one AWB.
export const selectionCheck = (selection, packages) => {
  const problems = {}
  selection.parcels.forEach((parcel, index) => {
    const weight = Number(parcel.weight)
    if (String(parcel.weight).trim() === "" || !Number.isFinite(weight) || weight < 0.01 || weight > WEIGHT_LIMIT) {
      problems[index] = `Enter a weight from 0.01 to ${WEIGHT_LIMIT} kg.`
    } else if (isEnvelope(packages, parcel) && weight > ENVELOPE_WEIGHT_LIMIT) problems[index] = "An envelope carries up to 1 kg."
  })
  const envelopes = selection.parcels.filter(parcel => isEnvelope(packages, parcel)).length
  const general = envelopes > ENVELOPES_LIMIT ? `An AWB takes at most ${ENVELOPES_LIMIT} envelopes.` : null
  return { problems, general, valid: selection.parcels.length > 0 && !Object.keys(problems).length && !general }
}
// What the API takes of a selection.
export const selectionBody = selection => ({ parcels: selection.parcels.map(({ packageId, weight }) => ({ packageId, weight: Number(weight) })),
  saturday: selection.saturday })
const totalWeight = selection => round(selection.parcels.reduce((sum, parcel) => sum + (Number(parcel.weight) || 0), 0))

// The parcels chosen for a shipment's AWB, each an active package of Settings (an envelope only for documents) with its weight, and
// Saturday delivery, which a processing with an AWB entered by hand does not choose (`saturday` false). Adding or removing a parcel, or
// changing its package, splits the shipment's weight again (`weights`, parcelWeights).
export default function ParcelSelector({ packages, selection, onChange, weights, documents, saturday = true }) {
  const choices = offered(packages, documents)
  const { problems, general } = selectionCheck(selection, packages)
  const reshape = parcels => onChange({ ...selection, parcels: split(parcels, packages, weights) })
  const change = (index, fields) => selection.parcels.map((parcel, position) => (position === index ? { ...parcel, ...fields } : parcel))
  return <>
    <Table size="sm" className="logistics-stack mb-1">
      <thead><tr><th>#</th><th>Package</th><th>Dimensions</th><th>Weight (kg)</th><th><span className="sr-only">Remove</span></th></tr></thead>
      <tbody>{selection.parcels.map((parcel, index) => {
        const chosen = packageOf(packages, parcel)
        return <tr key={index} role="group" aria-label={`Parcel ${index + 1}`}>
          <td>{index + 1}</td>
          <td data-label="Package">
            <Input type="select" bsSize="sm" aria-label="Package" value={parcel.packageId} onChange={event => reshape(change(index, { packageId: event.target.value }))}>
              {choices.map(item => <option key={item.id} value={item.id} disabled={!item.allowed}>
                {item.type === "envelope" ? `${item.name} · documents only` : item.name}
              </option>)}
            </Input>
          </td>
          <td data-label="Dimensions">{chosen && dimensionsLabel(chosen)}</td>
          <td data-label="Weight (kg)">
            <Input type="number" bsSize="sm" min={0.01} step={0.01} aria-label="Weight (kg)" value={parcel.weight} invalid={Boolean(problems[index])}
              onChange={event => onChange({ ...selection, parcels: change(index, { weight: event.target.value }) })} />
            {problems[index] && <FormFeedback>{problems[index]}</FormFeedback>}
          </td>
          <td>{selection.parcels.length > 1 && <Button color="link" size="sm" className="p-1 text-muted" aria-label={`Remove parcel ${index + 1}`} title="Remove"
            onClick={() => reshape(selection.parcels.filter((_, position) => position !== index))}><i className="bx bx-trash" aria-hidden="true" /></Button>}</td>
        </tr>
      })}</tbody>
    </Table>
    <div className="d-flex justify-content-between align-items-center flex-wrap mb-2">
      <Button color="link" className="p-0" disabled={selection.parcels.length >= PARCELS_LIMIT}
        onClick={() => reshape([...selection.parcels, { packageId: firstPackage(packages, documents).id }])}>
        <i className="bx bx-plus mr-1" aria-hidden="true" />Add parcel
      </Button>
      <span className="small">{`Total: ${totalWeight(selection)} kg`}</span>
    </div>
    <p className="small text-muted">The products' weight is split equally, plus each package's own weight; enter a parcel's weight after weighing it.
      Envelopes carry only documents (diplomas), up to 1 kg each.</p>
    {general && <p className="small text-danger">{general}</p>}
    {saturday && <FormGroup check className="mb-2">
      <Input id="logistics-saturday-delivery" type="checkbox" checked={selection.saturday} onChange={event => onChange({ ...selection, saturday: event.target.checked })} />
      <Label check for="logistics-saturday-delivery">Saturday delivery</Label>
    </FormGroup>}
  </>
}
